import React from 'react';
import './Input.css';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
    label?: string;
    error?: string;
    helperText?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    fullWidth?: boolean;
    size?: 'sm' | 'md' | 'lg';
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className = '',
            label,
            error,
            helperText,
            leftIcon,
            rightIcon,
            fullWidth = true,
            size = 'md',
            id,
            ...props
        },
        ref
    ) => {
        const generatedId = React.useId();
        const inputId = id || generatedId;

        const baseClasses = 'qube-input';
        const sizeClasses = `qube-input-${size}`;
        const errorClasses = error ? 'qube-input-error' : '';
        const iconLeftClasses = leftIcon ? 'qube-input-has-left-icon' : '';
        const iconRightClasses = rightIcon ? 'qube-input-has-right-icon' : '';

        const combinedClasses = [
            baseClasses,
            sizeClasses,
            errorClasses,
            iconLeftClasses,
            iconRightClasses,
            className
        ].filter(Boolean).join(' ');

        const wrapperClasses = `qube-input-wrapper ${fullWidth ? 'qube-input-full' : ''}`;

        return (
            <div className={wrapperClasses}>
                {label && (
                    <label htmlFor={inputId} className="qube-input-label">
                        {label}
                        {props.required && <span className="qube-input-required">*</span>}
                    </label>
                )}

                <div className="qube-input-container">
                    {leftIcon && <span className="qube-input-icon-left">{leftIcon}</span>}

                    <input
                        ref={ref}
                        id={inputId}
                        className={combinedClasses}
                        aria-invalid={!!error}
                        aria-describedby={
                            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
                        }
                        {...props}
                    />

                    {rightIcon && <span className="qube-input-icon-right">{rightIcon}</span>}
                </div>

                {error && (
                    <p id={`${inputId}-error`} className="qube-input-error-text">
                        {error}
                    </p>
                )}

                {!error && helperText && (
                    <p id={`${inputId}-helper`} className="qube-input-helper-text">
                        {helperText}
                    </p>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';
